
import React from 'react';
import { useAppSettings } from '@/hooks/useAppSettings';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, Settings, Clock } from 'lucide-react';

interface MaintenanceGuardProps {
  children: React.ReactNode;
}

const MaintenancePage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-black/40 border-gray-800">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 p-3 rounded-full bg-yellow-500/20">
            <Settings className="h-8 w-8 text-yellow-500" />
          </div>
          <CardTitle className="text-white text-2xl">
            Manutenção em Andamento
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <div className="flex items-center justify-center gap-2 text-yellow-400">
            <AlertTriangle className="h-5 w-5" />
            <span className="text-sm">Sistema temporariamente indisponível</span>
          </div>
          
          <p className="text-gray-300 text-sm leading-relaxed">
            Estamos realizando melhorias na plataforma para oferecer uma melhor experiência. 
            Voltaremos em breve!
          </p>
          
          <div className="flex items-center justify-center gap-2 text-gray-400 text-xs">
            <Clock className="h-4 w-4" />
            <span>Tempo estimado: poucos minutos</span>
          </div>
          
          <div className="pt-4 border-t border-gray-700">
            <p className="text-xs text-gray-500">
              Em caso de urgência, entre em contato com o suporte
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const MaintenanceGuard: React.FC<MaintenanceGuardProps> = ({ children }) => {
  const { isMaintenanceMode, loading } = useAppSettings();
  const { user, profileData } = useAuth();
  
  // ✅ Hooks sempre no topo, sem condições
  const [isAdmin, setIsAdmin] = React.useState<boolean | null>(null);
  const [checkingAdmin, setCheckingAdmin] = React.useState(false);
  
  // ✅ useEffect sempre executa, mesmo se user não existe
  React.useEffect(() => {
    if (!profileData?.id) {
      setIsAdmin(null);
      return;
    }
    
    setCheckingAdmin(true);
    
    // Usar AdminService para verificação consistente
    import('@/core/services/AdminService').then(({ AdminService }) => {
      AdminService.isAdmin(profileData.id)
        .then((result) => {
          setIsAdmin(result);
          setCheckingAdmin(false);
        })
        .catch(() => {
          setIsAdmin(false);
          setCheckingAdmin(false);
        });
    });
  }, [profileData?.id]);

  // Loading inicial do app settings
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center">
        <div className="text-white">Carregando...</div>
      </div>
    );
  }

  // Se usuário não está autenticado, renderizar normalmente (permite login)
  if (!user) {
    return <>{children}</>;
  }

  // Se está em modo de manutenção
  if (isMaintenanceMode()) {
    // Aguardando verificação de admin
    if (checkingAdmin || isAdmin === null) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center">
          <div className="text-white">Verificando permissões...</div>
        </div>
      );
    }
    
    // Usuário não é admin: mostrar tela de manutenção
    if (isAdmin === false) {
      return <MaintenancePage />;
    }
  }

  // Caso contrário, renderizar normalmente
  return <>{children}</>;
};
